import * as Yup from 'yup';

import { WEBHOOK_AUTH_TYPE } from '../types';

interface ICredentialsSchemaOptions {
  originalAuthType?: string;
}

type CredentialParent = {
  auth_type: string;
  basic_username?: string;
  basic_password?: string;
  header_name?: string;
  header_value?: string;
  query_parameter?: string;
  query_value?: string;
};

const hasValue = (v: string | undefined): boolean =>
  typeof v === 'string' && v.length > 0;

const credentialRequired = (
  authType: string,
  targetType: string | string[],
  originalAuthType: string | undefined,
  siblingValue: string | undefined
): boolean => {
  const matchesType = Array.isArray(targetType)
    ? targetType.includes(authType)
    : authType === targetType;
  if (!matchesType) return false;
  // Create mode: always required
  if (originalAuthType === undefined) return true;

  // Edit mode: required when auth type changed or sibling field already filled
  return authType !== originalAuthType || hasValue(siblingValue);
};

export const buildWebhookCredentialsSchema = (
  options?: ICredentialsSchemaOptions
) => {
  const { originalAuthType } = options ?? {};

  return Yup.object({
    auth_type: Yup.string().required('Auth type is required'),
    basic_username: Yup.string().test(
      'basic-username-required',
      'Username is required',
      function (value) {
        const parent = this.parent as CredentialParent;

        return (
          !credentialRequired(
            parent.auth_type,
            WEBHOOK_AUTH_TYPE.BASIC,
            originalAuthType,
            parent.basic_password
          ) || hasValue(value)
        );
      }
    ),
    basic_password: Yup.string().test(
      'basic-password-required',
      'Password is required',
      function (value) {
        const parent = this.parent as CredentialParent;

        return (
          !credentialRequired(
            parent.auth_type,
            WEBHOOK_AUTH_TYPE.BASIC,
            originalAuthType,
            parent.basic_username
          ) || hasValue(value)
        );
      }
    ),
    header_name: Yup.string().test(
      'header-name-required',
      'Header name is required',
      function (value) {
        const parent = this.parent as CredentialParent;

        return (
          !credentialRequired(
            parent.auth_type,
            [WEBHOOK_AUTH_TYPE.HEADER, WEBHOOK_AUTH_TYPE.API_KEY],
            originalAuthType,
            parent.header_value
          ) || hasValue(value)
        );
      }
    ),
    header_value: Yup.string().test(
      'header-value-required',
      'Header value is required',
      function (value) {
        const parent = this.parent as CredentialParent;

        return (
          !credentialRequired(
            parent.auth_type,
            [WEBHOOK_AUTH_TYPE.HEADER, WEBHOOK_AUTH_TYPE.API_KEY],
            originalAuthType,
            parent.header_name
          ) || hasValue(value)
        );
      }
    ),
    query_parameter: Yup.string().test(
      'query-parameter-required',
      'Query parameter is required',
      function (value) {
        const parent = this.parent as CredentialParent;

        return (
          !credentialRequired(
            parent.auth_type,
            WEBHOOK_AUTH_TYPE.QUERY_PARAMETER,
            originalAuthType,
            parent.query_value
          ) || hasValue(value)
        );
      }
    ),
    query_value: Yup.string().test(
      'query-value-required',
      'Query value is required',
      function (value) {
        const parent = this.parent as CredentialParent;

        return (
          !credentialRequired(
            parent.auth_type,
            WEBHOOK_AUTH_TYPE.QUERY_PARAMETER,
            originalAuthType,
            parent.query_parameter
          ) || hasValue(value)
        );
      }
    ),
  });
};
